// Copyright ©️ 2026 eVolve MEP, LLC

import type { AssignedCustomProperty, UpsertCustomProperty } from '../CustomProperty/types';
import type { FacilityId } from '../Facility/types';
import type { ISOTimestamp } from '../Misc/dateOnly';
import type { ProjectId } from '../Project/types';
import type { WorkOrderId, WorkOrderItemId } from '../WorkOrder/types';

declare const machineProfileIdSymbol: unique symbol;
export type MachineProfileId = string & { [machineProfileIdSymbol]: never };

declare const machineIdSymbol: unique symbol;
export type MachineId = string & { [machineIdSymbol]: never };

export type Machine = {
  machineId: MachineId;
  name: string;
  machineType: string;
};

export type MachineProfileType = 'crippa150le' | 'manualBender';

export type MachineProfile<P extends MachineProfileType = MachineProfileType> = {
  machineProfileId: MachineProfileId;
  machineId: MachineId;
  machineName: string;
  name: string;
  profileType: P;
  conduitManufacturer: string;
};

/** The angle identifies the calibration within its conduit type and size; there is no surrogate id. */
export type ProfileCalibration = {
  /** Degrees. */
  angle: number;
  /** Degrees. */
  springBack: number;
  /** Decimal feet. */
  elongation: number;
};

export type BenderProfileConfiguration = {
  conduitType: ConduitType;
  conduitSize: ConduitSize;
  /** Decimal feet. */
  measuredRadius: number;
  /** Decimal feet. */
  toolingRadius: number | null;
  /** Decimal feet. */
  mark1: number | null;
  /** Decimal feet. */
  deduct: number | null;
  /** Decimal feet. */
  xValue: number | null;
  /** Decimal feet. */
  materialOutsideDiameter: number;
  /** Decimal feet. */
  materialThickness: number;
  /** Decimal feet */
  materialStockLength: number;
  calibrations: ProfileCalibration[];
};

/** Covers every bender family; values that apply only to the other family are null. */
export type BenderProfile = {
  machineProfile: MachineProfile;
  configurationValues: BenderProfileConfiguration[];
};

/** The full desired state of a bender profile's configurations. Omitted angles are removed. */
export type UpdateBenderProfileBody = {
  configurationValues: {
    conduitTypeId: ConduitTypeId;
    conduitSizeId: ConduitSizeId;
    measuredRadius: number;
    /** Decimal feet */
    materialStockLength: number;
    toolingRadius?: number | null;
    mark1?: number | null;
    deduct?: number | null;
    xValue?: number | null;
    calibrations: ProfileCalibration[];
  }[];
};

declare const conduitTypeIdSymbol: unique symbol;
export type ConduitTypeId = string & { [conduitTypeIdSymbol]: never };

export type ConduitType = {
  conduitTypeId: ConduitTypeId;
  name: string;
};

declare const conduitSizeIdSymbol: unique symbol;
export type ConduitSizeId = string & { [conduitSizeIdSymbol]: never };

export type ConduitSize = {
  conduitSizeId: ConduitSizeId;
  name: string;
  diameter: number;
};

declare const elementIdSymbol: unique symbol;
export type ElementId = string & { [elementIdSymbol]: never };

export type FabricationAction = 'Bend' | 'UndoBend' | 'Cut' | 'UndoCut';

/**
 * @remarks 'NotApplicable' means the bend has not been validated (no bender profile has been assigned).
 */
export type BendValidationStatus = 'NotApplicable' | 'Pass' | 'Fail';

/** A machine minimum a bend is checked against. */
export type BendValidationRequirement =
  | 'MinimumFrontStraightLength'
  | 'MinimumStraightBetweenBends'
  | 'MinimumFinalStraightLength';

/** One machine minimum a bend failed, with the value measured on the bend and the minimum it missed. */
export type BendValidationFailure = {
  requirement: BendValidationRequirement;
  /** The failure message as worded by the bend calculation package, which owns this wording. */
  message: string;
  /** The value measured on the bend, in decimal feet. */
  actual: number;
  /**
   * The minimum the bend had to meet, in decimal feet. Already includes the profile adjustments — Mark 1
   * added to the front straight, and the final bend's arc subtracted from the final straight.
   */
  minimum: number;
};

export type MachineElement = {
  elementId: ElementId;
  name: string;
  projectId: ProjectId;
  projectName: string;
  projectIdentifier: string;
  sourceIds: string | null;
  customerId: string | null;
  runId: string | null;
  // in decimal feet
  length: number;
  adjustedLength?: number;
  spool: string | null;
  location: string | null;
  level: string | null;
  // Expected to be one of BendTypeName, but needs to be validated
  bendType: string | null;
  materialType: string;
  // Nominal string, i.e. `3-1/2"`, validated on the backend.
  materialSize: string;
  workOrderItemId: WorkOrderItemId | null;
  workOrderId: WorkOrderId | null;
  workOrderIdentifier: string | null;
  workOrderName: string | null;
  canMarkBendDone: boolean;
  canMarkCutDone: boolean;
  hasBendData: boolean;
  comments?: string;
  /** When the element's bend work was marked done, or null if it has not been. */
  bendMarkedDoneOn: ISOTimestamp | null;
  /** When the element's cut work was marked done, or null if it has not been. */
  cutMarkedDoneOn: ISOTimestamp | null;
  source: string | null;
  benderProfile: MachineProfile | null;
  bendValidationStatus: BendValidationStatus;
  /** The machine minimums the bend failed, one entry each. Empty unless bendValidationStatus is 'Fail'. */
  bendValidationErrors: BendValidationFailure[];
  /** When the bend data was last changed by an external source (i.e. Revit), or null if it never has been. */
  machineDataExternalUpdatedOn: ISOTimestamp | null;
  /** When the bend data was last changed from the web application, or null if it never has been. */
  machineDataWebUpdatedOn: ISOTimestamp | null;
  cutListId: CutListId | null;
  /** The element's position in its cut list's optimized layout (e.g. `S2-C3`), or null when the cut optimization has not run. */
  cutIndex: string | null;
  /** The cut profile assigned to the cut list for this element, or null if it has not been assigned. */
  cutProfile: CutProfile | null;
  /** Why the cut optimization could not place this element, or null when it has not failed. */
  cutOptimizationError: string | null;
  customProperties: AssignedCustomProperty[];
};

export const bendInputKeys = [
  'angle',
  'stubHeight',
  'legLength',
  'distanceToObstruction',
  'offsetHeight',
  'takeUpLength',
  'kickHeight',
  'centerToEnd1',
  'centerToEnd2',
  'saddleHeight',
  'distanceToCenterOfObstruction',
  'saddleLength',
  'noseLength',
  'tailLength',
] as const;
export type BendInputKey = (typeof bendInputKeys)[number];

export type BendTypeInput = {
  name: string;
  keyName: BendInputKey;
  isRequired: boolean;
  isAngle: boolean;
};

const bendTypeNames = [
  'Stub 90',
  'Offset',
  'Kick 90 Left',
  'Kick 90 Right',
  'Angled',
  '3-Point Saddle',
  '4-Point Saddle',
  'Offset Back to Back',
  'Kick 90 Back to Back Right',
  'Kick 90 Back to Back Left',
  'Custom',
] as const;
export type BendTypeName = (typeof bendTypeNames)[number];
export const isValidBendType = (bendType: BendTypeName | string): bendType is BendTypeName =>
  bendTypeNames.includes(bendType as BendTypeName);

export type BendType = {
  name: BendTypeName;
  stockLengthKeyName: BendInputKey;
  inputs: BendTypeInput[];
};

export type BendCreatorValidationRequest = {
  benderProfileId: MachineProfileId;
  bendType: BendTypeName;
  conduitTypeId: ConduitTypeId;
  conduitSizeId: ConduitSizeId;
  materialStockLength: number;
  maximizeLegLength: boolean;
} & Record<BendInputKey, number | null>;

export type ElementUpdateRequest = {
  bendData?: BendCreatorValidationRequest;
  elementData?: {
    location?: string;
    level?: string;
    runId?: string;
    description?: string;
    spool?: string;
  };
  customProperties?: UpsertCustomProperty[];
};

export type BendCreatorCreateRequest = {
  partName: string;
  quantity: number;
  workOrderId: WorkOrderId;
  location?: string;
  level?: string;
  runId?: string;
  description?: string;
} & BendCreatorValidationRequest;

declare const ybcSymbol: unique symbol;
export type YBCString = string & { [ybcSymbol]: never };

export type BendData = {
  bendType: BendTypeName;
  bendRadius: number;
  finalDimensions: {
    keyName: BendInputKey;
    label: string;
    isAngle: boolean;
    value: number;
  }[];
  layoutDimensions: {
    label: string;
    value: number;
  }[];
  ybcData?: YBCString;
  springBackAngle?: number | null;
};

export type BendFlag = 'None' | 'Unconverted' | 'Trimmed' | 'TooShort' | 'TailTooShort' | 'TailSwapped';

export type BendValidationResponse = {
  flag: BendFlag;
  bendRadius: number;
  parsedBendFlags: {
    keyName: string;
    message: string;
  }[];
  updatedBend: {
    keyName: BendInputKey;
    value: number;
  }[];
  validationErrors: string[];
};

declare const machineTypeSettingIdSymbol: unique symbol;
export type MachineTypeSettingId = string & { [machineTypeSettingIdSymbol]: never };

/** The minimums a machine type configuration row carries. Coverage is fixed at creation. */
type MachineTypeConfigMinimums = {
  /** in decimal feet */
  minimumFrontStraight: number;
  /** in decimal feet */
  minimumStraightBetweenBends: number;
  /** in decimal feet */
  minimumFinalStraight: number;
};

export type CreateMachineTypeConfigRequest = {
  machineId: MachineId;
  conduitSizeId: ConduitSizeId;
  conduitTypeIds: ConduitTypeId[];
  bendTypes: BendTypeName[];
};

export type MachineTypeConfig = MachineTypeConfigMinimums &
  CreateMachineTypeConfigRequest & {
    machineTypeSettingId: MachineTypeSettingId;
  };

/** A row's coverage cannot be changed; delete and re-create instead. */
export type UpdateMachineTypeConfigRequest = MachineTypeConfigMinimums & {
  machineTypeSettingId: MachineTypeSettingId;
};

export type SyncMachineTypeConfigRequest = {
  updateConfigurationValues: UpdateMachineTypeConfigRequest[];
  deleteConfigurationValues: MachineTypeSettingId[];
};

declare const cutProfileIdSymbol: unique symbol;
export type CutProfileId = string & { [cutProfileIdSymbol]: never };

/** The method a cut profile uses when laying cuts out across stock lengths. */
export type CutOptimizationMethod = 'MinimizeScrap';

/** Every length on a cut profile is in decimal feet, matching machine element lengths. */
export type CutProfile = {
  cutProfileId: CutProfileId;
  name: string;
  /** The length of each stock piece the cuts are laid out across, in decimal feet. */
  stockLength: number;
  /** The material removed from the start of each stock length, in decimal feet. */
  startTrim: number | null;
  /** The material removed from the end of each stock length, in decimal feet. */
  endTrim: number | null;
  /** The width of the saw blade — the material removed between cuts, in decimal feet. */
  kerfWidth: number;
  /** The allowable deviation from an element's exact length, in decimal feet. */
  tolerance: number;
  optimizationMethod: CutOptimizationMethod;
};

declare const cutListIdSymbol: unique symbol;
export type CutListId = string & { [cutListIdSymbol]: never };

/** The elements of one material type and size on a work order, laid out across stock lengths by a cut profile. */
export type CutList = {
  cutListId: CutListId;
  workOrderId: WorkOrderId;
  workOrderIdentifier: string;
  workOrderName: string;
  cutProfile: CutProfile | null;
  cutCount: number;
  name: string;
  materialType: string;
  /** Nominal string, i.e. `3-1/2"`, validated on the backend. */
  materialSize: string;
  /** The number of stock lengths the cut optimization used, or null when it has not run. */
  optimizedStockLengths: number | null;
  /** The total stock length the cut optimization used, in decimal feet, or null when it has not run. */
  totalUsedStockLength: number | null;
  hasCutOptimizationErrors: boolean;
};

/**
 * The result of assigning a cut profile to a cut list: the list's optimization totals plus the outcome for
 * each of its elements.
 */
export type CutListOptimization = {
  cutListId: CutListId;
  cutProfileId: CutProfileId;
  optimizedStockLengths: number;
  /** The total stock length the optimization used, in decimal feet. */
  totalUsedStockLength: number;
  elements: {
    elementId: ElementId;
    /** The element's position in the optimized layout (e.g. `S2-C3`), or null when it could not be placed. */
    cutIndex: string | null;
    /** Why the element could not be placed, or null when it was. */
    cutOptimizationError: string | null;
  }[];
};

/** Which kinds of fabrication work a facility performs. */
export type FacilityFabricationSetting = {
  facilityId: FacilityId;
  /** Whether elements on the facility's work orders must be cut before they are complete. */
  isCuttingEnabled: boolean;
  /** Whether elements on the facility's work orders must be bent before they are complete. */
  isBendingEnabled: boolean;
};
